import { useState, useCallback } from "react";
import { Copy, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { copyToClipboard } from "@/lib/copyToClipboard";
import { cn } from "@/lib/utils";

interface CopyButtonProps {
  /** Text to copy to clipboard. */
  text: string;
  /** Optional custom tooltip / aria-label. */
  label?: string;
  /** Size class for the icon — defaults to "h-3.5 w-3.5". */
  iconClassName?: string;
  /** Extra classes on the wrapper button. */
  className?: string;
}

/**
 * Small icon button that copies `text` to the clipboard and shows a
 * brief checkmark feedback + a sonner toast.
 */
export function CopyButton({
  text,
  label,
  iconClassName = "h-3.5 w-3.5",
  className,
}: CopyButtonProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      await copyToClipboard(text, t("common.copied"));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    },
    [text, t],
  );

  return (
    <button
      type="button"
      onClick={handleCopy}
      aria-label={label ?? t("common.copy")}
      className={cn(
        "inline-flex items-center justify-center rounded p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors cursor-pointer",
        className,
      )}
    >
      {copied ? (
        <Check className={cn(iconClassName, "text-green-500")} />
      ) : (
        <Copy className={iconClassName} />
      )}
    </button>
  );
}
